<template>
  <v-card class="grid-container mb-2" elevation="2">
    <div class="aoi center-container mt-3 mb-3">
      <div class="aoi-content">
        <div class="aoi-name-holder ml-1">
          <DetailsListItemClickable
            v-show="flagImgSrc"
            style="line-height: 1.2em; text-align: center;"
            :content="countryNameLink"
            :analytics-category="analyticsCategory"
          />
        </div>
        <div><v-img :src="flagImgSrc" class="flag mx-auto" /></div>
      </div>
    </div>
    <div class="hotspots-holder">
      <div class="hotspots hyp-scroll">
        <div
          class="hotspot pa-3"
          v-for="(hotspot, index) in country.hotspots"
          :key="index"
        >
          <div @click="doMapClick(hotspot)">
            <v-card-title class="hotspot-title pa-0 mb-5">{{
              getHotspotTitle(hotspot)
            }}</v-card-title>
            <v-card-subtitle class="hotspot-topic-display pa-0">
              <strong>Event:</strong> {{ getHotspotTopic(hotspot) }}
            </v-card-subtitle>
            <v-card-subtitle class="hotspot-date-display pa-0">
              {{ getHotspotStartTime(hotspot) }}
            </v-card-subtitle>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import lodashGet from "lodash/get";
import {
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_HOTSPOTS,
  MODULE_PREFIX_MAP,
} from "@/store";
import { GET_DETAILS_SIDEBAR } from "@/store/actions.type";
import { mapGetters } from "vuex";
import { SET_HOTSPOTS_DETAILS } from "@/store/mutations.type";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { LeftSidePanelWidthNumber } from "@/common/constants/layout";
import { EVENT_ACTIONS, LATEST_CATEGORIES } from "@/common/constants/analytics";
import { points, bbox } from "@turf/turf";

import { getCountryByCode } from "@/common/maps/utilities";
import { fakeMapFeature } from "@/common/maps/features";
import { formatFlagUrl } from "@/common/basic.utilities";

const EMITTED_ACTIONS = {
  OPEN_HOTSPOT_DETAILS: "open-hotspot-details",
};

export default {
  name: "LatestHotspotCountryListing",
  props: {
    country: {
      type: Object,
      required: true,
    },
  },
  data: function () {
    return {
      //TODO this is redundant with something in DeatilsSidebar. It should be factored out
      leftSidebarWidth:
        this.featureUnlocked("left-panel-mini") && this.leftPanelMinimized
          ? 45
          : LeftSidePanelWidthNumber,
      countryData: null,
      flagImgSrc: null,
      analyticsCategory: LATEST_CATEGORIES.hotspots,
    };
  },
  components: {
    DetailsListItemClickable: () =>
      import("@/components/common/details/DetailsListItemClickable"),
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    formatted() {
      let formatted = lodashGet(this.country, "properties.formatted");
      if (formatted) {
        return formatted;
      }
      return null;
    },
    body() {
      return lodashGet(this.formatted, "summary");
    },
    dateTime() {
      if (this.formatted && this.formatted.occurred) {
        return getFormatted(this.formatted.occurred);
      }
      return null;
    },
    countryNameLink() {
      const countryName = lodashGet(this.countryData, "properties.displayName");
      const countryCode = lodashGet(this.countryData, "properties.iso2");
      let countryText = `<a data-country-code="${countryCode}">`;
      countryText += ` <span class="mr-1" data-country-code="${countryCode}">${countryName}</span></a>`;
      return countryText;
    },
  },
  async mounted() {
    await this.updateCountry();
  },
  methods: {
    doMapClick(hotspot) {
      let details = {
        feature: fakeMapFeature(hotspot),
      };

      this.$store.commit(
        MODULE_PREFIX_HOTSPOTS + SET_HOTSPOTS_DETAILS,
        details
      );

      this.detailsSidebar.features = [fakeMapFeature(hotspot)];
      this.detailsSidebar.visible = true;
      this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.monitor;
      this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.monitor;
      this.detailsSidebar.monitorType = "hotspots";
      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );

      this.$emit(EMITTED_ACTIONS.OPEN_HOTSPOT_DETAILS, true);
      //Pan and zoom the map to the bounding box with some padding so that hotspots aren't on the edge of the map.

      this.map.flyTo({
        zoom: 11,
        center: hotspot.geometry.coordinates,
        padding: {
          top: 100,
          bottom: 100,
          left: 100,
          right:
            (this.leftSidebarWidth || 0) +
            (this.detailsSidebar.width || 0) +
            100,
        },
      });

      //Get information for the analytics tracker
      const location = fakeMapFeature(hotspot)?.properties?.location_metadata;
      let analyticsDetails = "";
      if (location) {
        analyticsDetails = JSON.parse(location)?.country;
        if (analyticsDetails) {
          analyticsDetails += ". Country: " + analyticsDetails;
        }
      }
      this.handlePageTrackerEvent(
        LATEST_CATEGORIES.hotspots,
        EVENT_ACTIONS.clicked,
        `hotspot event ${analyticsDetails}`
      );
    },
    getHotspotTitle(hotspot) {
      return lodashGet(hotspot, "properties.headline.title");
    },
    getHotspotStartTime(hotspot) {
      return lodashGet(hotspot, "properties.formatted.startTime");
    },
    getHotspotTopic(hotspot) {
      return lodashGet(hotspot, "properties.topics")
        .join(" | ")
        .replace("_", " ");
    },
    async updateCountry() {
      let iso2 = lodashGet(this.country, "iso2");
      if (iso2) {
        this.countryData = JSON.parse(await getCountryByCode(iso2)).data;
        this.flagImgSrc = formatFlagUrl(
          lodashGet(this.countryData, "properties.iso3")
        );
      } else {
        console.error("unable to determine iso2 for country");
      }
    },
  },
  watch: {
    async country() {
      await this.updateCountry();
    },
  },
};
</script>

<style scoped>
.center-container {
  display: flex;
  justify-content: center;
}

.grid-container {
  display: grid;
  grid-template-columns: 120px 1fr;
  grid-template-areas: "aoi hotspots";
}

.aoi {
  grid-area: aoi;
  border-style: solid;
  border-width: 0 1px 0 0;
  border-color: rgb(0 0 0 / 20%);
  width: 100%;
  height: 100%;
}

.aoi-content {
  margin: 10px;
}

.aoi-name-holder {
  margin-bottom: 6px;
}

.hotspots-holder {
  margin: auto 0 auto 0;
}

.hotspots {
  grid-area: hotspots;
}

.hotspot {
  flex-grow: 0;
  flex-shrink: 0;

  display: flex;
  flex-direction: row;
  align-items: center;

  line-height: 0.9em;

  cursor: pointer;
}

>>> .v-card__subtitle {
  font-size: x-small;
}
.hotspot-title {
  font-size: 0.9em;
  line-height: 1.4em;
  word-break: break-word;
}
.flag {
  height: 35px;
  max-height: 35px;
  width: 35px;
  max-width: 35px;
  border-radius: 18px;
}
</style>
